import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { cn } from './cn';

// Small, dependency-free SVG charts. One series per chart (no dual axes), 2px
// lines, hairline grid, a crosshair tooltip, and an aria summary for screen readers.

function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const observer = new ResizeObserver(entries => setWidth(entries[0]?.contentRect.width ?? 0));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

const timeLabel = new Intl.DateTimeFormat(undefined, { hour: 'numeric' });
const dayLabel = new Intl.DateTimeFormat(undefined, { weekday: 'short' });
const fullLabel = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

export interface Point { at: string; value: number | null }

/** Splits a series into runs without gaps, so missing data is drawn as missing. */
function segments(points: Array<{ x: number; y: number | null }>) {
  const runs: Array<Array<{ x: number; y: number }>> = [];
  let current: Array<{ x: number; y: number }> = [];
  for (const p of points) {
    if (p.y === null) { if (current.length) runs.push(current); current = []; } else current.push({ x: p.x, y: p.y });
  }
  if (current.length) runs.push(current);
  return runs;
}

export function TimeSeriesChart({ points, label, unit = '%', max = 100, threshold, height = 160, range }: {
  points: Point[]; label: string; unit?: string; max?: number; threshold?: { value: number; label: string }; height?: number; range: '24h' | '7d';
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const pad = { left: 42, right: 14, top: 10, bottom: 22 };
  const w = Math.max(0, width - pad.left - pad.right), h = height - pad.top - pad.bottom;
  const end = Date.now(), start = end - (range === '24h' ? 86_400_000 : 7 * 86_400_000);
  const x = (t: number) => pad.left + ((t - start) / (end - start)) * w;
  const y = (v: number) => pad.top + h - (Math.min(v, max) / max) * h;
  const plotted = points.map(p => ({ t: Date.parse(p.at), v: p.value })).filter(p => p.t >= start);
  const runs = segments(plotted.map(p => ({ x: x(p.t), y: p.v === null ? null : y(p.v) })));
  const values = plotted.map(p => p.v).filter((v): v is number => v !== null);
  const avg = values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
  const peak = values.length ? Math.max(...values) : null;
  const ticks = range === '24h'
    ? Array.from({ length: 4 }, (_, i) => end - (3 - i) * 6 * 3_600_000)
    : Array.from({ length: 7 }, (_, i) => end - (6 - i) * 86_400_000);
  const hovered = hover !== null ? plotted[hover] : null;
  const gradient = useId();
  const onMove = (event: React.PointerEvent<SVGSVGElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    const t = start + ((event.clientX - box.left - pad.left) / w) * (end - start);
    let best = -1, distance = Infinity;
    plotted.forEach((p, i) => { const d = Math.abs(p.t - t); if (d < distance) { distance = d; best = i; } });
    setHover(best >= 0 ? best : null);
  };
  const summary = values.length ? `${label}: average ${avg!.toFixed(0)}${unit}, peak ${peak!.toFixed(0)}${unit} over the last ${range === '24h' ? '24 hours' : '7 days'}` : `${label}: no samples in this period`;

  return (
    <div ref={ref} className="relative w-full" style={{ height }}>
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label={summary} onPointerMove={onMove} onPointerLeave={() => setHover(null)} className="touch-none">
          <defs>
            <linearGradient id={gradient} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="var(--c-series)" stopOpacity="0.18" /><stop offset="100%" stopColor="var(--c-series)" stopOpacity="0" />
            </linearGradient>
          </defs>
          {[0, 50, 100].filter(v => v <= max).map(v => (
            <g key={v}>
              <line x1={pad.left} x2={pad.left + w} y1={y(v)} y2={y(v)} stroke="var(--c-line)" strokeWidth={1} />
              <text x={pad.left - 6} y={y(v) + 3.5} textAnchor="end" className="fill-ink-3 text-[10.5px] tabular">{v}{unit}</text>
            </g>
          ))}
          {ticks.map((t, i) => (
            <text key={t} x={x(t)} y={height - 6} textAnchor={i === ticks.length - 1 ? 'end' : 'middle'} className="fill-ink-3 text-[10.5px]">
              {range === '24h' ? timeLabel.format(t) : dayLabel.format(t)}
            </text>
          ))}
          {threshold && (
            <g>
              <line x1={pad.left} x2={pad.left + w} y1={y(threshold.value)} y2={y(threshold.value)} stroke="var(--c-attention)" strokeWidth={1} strokeDasharray="4 4" />
              <text x={pad.left + 4} y={y(threshold.value) + 12} className="fill-ink-3 text-[10.5px]">{threshold.label}</text>
            </g>
          )}
          {runs.map((run, i) => (
            <g key={i}>
              <path d={`M${run[0]!.x},${pad.top + h} ${run.map(p => `L${p.x},${p.y}`).join(' ')} L${run.at(-1)!.x},${pad.top + h}Z`} fill={`url(#${gradient})`} />
              <path d={run.map((p, j) => `${j ? 'L' : 'M'}${p.x},${p.y}`).join(' ')} fill="none" stroke="var(--c-series)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
            </g>
          ))}
          {hovered && (
            <g pointerEvents="none">
              <line x1={x(hovered.t)} x2={x(hovered.t)} y1={pad.top} y2={pad.top + h} stroke="var(--c-line-strong)" strokeWidth={1} />
              {hovered.v !== null && <circle cx={x(hovered.t)} cy={y(hovered.v)} r={4.5} fill="var(--c-series)" stroke="var(--c-panel)" strokeWidth={2} />}
            </g>
          )}
        </svg>
      )}
      {hovered && (
        <div className="pointer-events-none absolute top-0 z-10 rounded-md border border-line bg-raised px-2 py-1 text-[12px] shadow-pop"
          style={{ left: Math.min(Math.max(x(hovered.t) - 60, 0), width - 130) }}>
          <span className="text-ink-3">{fullLabel.format(hovered.t)}</span>
          <span className="ml-2 font-semibold tabular">{hovered.v === null ? 'no data' : `${hovered.v.toFixed(1)}${unit}`}</span>
        </div>
      )}
      {!values.length && width > 0 && <p className="absolute inset-0 flex items-center justify-center text-[13px] text-ink-3">No samples in this period</p>}
    </div>
  );
}

export function Sparkline({ values, max = 100, className }: { values: Array<number | null>; max?: number; className?: string }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const height = 28;
  const pts = values.map((v, i) => ({ x: values.length > 1 ? (i / (values.length - 1)) * width : 0, y: v === null ? null : height - 2 - (Math.min(v, max) / max) * (height - 4) }));
  return (
    <div ref={ref} className={cn('h-7 w-full', className)} aria-hidden>
      {width > 0 && (
        <svg width={width} height={height}>
          {segments(pts).map((run, i) => <path key={i} d={run.map((p, j) => `${j ? 'L' : 'M'}${p.x},${p.y}`).join(' ')} fill="none" stroke="var(--c-series)" strokeWidth={1.5} strokeLinejoin="round" />)}
        </svg>
      )}
    </div>
  );
}

/** Free space over the last week, with the projected path to full drawn dashed. */
export function FreeSpaceChart({ history, totalBytes, daysToFull, height = 120 }: {
  history: Array<{ at: string; freeBytes: number }>; totalBytes: number; daysToFull: number | null; height?: number;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const pad = { left: 6, right: 6, top: 8, bottom: 20 };
  const now = Date.now(), start = now - 7 * 86_400_000;
  const projected = daysToFull !== null && daysToFull <= 14 ? now + daysToFull * 86_400_000 : null;
  const end = projected ?? now;
  const w = Math.max(0, width - pad.left - pad.right), h = height - pad.top - pad.bottom;
  const x = (t: number) => pad.left + ((t - start) / (end - start)) * w;
  const y = (v: number) => pad.top + h - (v / totalBytes) * h;
  const pts = history.map(p => ({ t: Date.parse(p.at), v: p.freeBytes })).filter(p => p.t >= start);
  const last = pts.at(-1);
  return (
    <div ref={ref} className="w-full" style={{ height }}>
      {width > 0 && pts.length > 1 && (
        <svg width={width} height={height} role="img" aria-label={`Free space over the last week${projected ? `; projected full around ${fullLabel.format(projected)}` : ''}`}>
          <line x1={pad.left} x2={pad.left + w} y1={pad.top + h} y2={pad.top + h} stroke="var(--c-line)" />
          <path d={pts.map((p, i) => `${i ? 'L' : 'M'}${x(p.t)},${y(p.v)}`).join(' ')} fill="none" stroke="var(--c-series)" strokeWidth={2} strokeLinejoin="round" />
          {projected && last && (
            <g>
              <path d={`M${x(last.t)},${y(last.v)} L${x(projected)},${y(0)}`} fill="none" stroke="var(--c-offline)" strokeWidth={2} strokeDasharray="5 4" />
              <circle cx={x(projected)} cy={y(0)} r={4} fill="var(--c-offline)" stroke="var(--c-panel)" strokeWidth={2} />
              <text x={Math.min(x(projected), pad.left + w)} y={height - 5} textAnchor="end" className="fill-ink-2 text-[10.5px] font-medium">Full ≈ {dayLabel.format(projected)} {new Date(projected).getDate()}</text>
            </g>
          )}
          <text x={pad.left} y={height - 5} className="fill-ink-3 text-[10.5px]">7 days ago</text>
          {!projected && <text x={pad.left + w} y={height - 5} textAnchor="end" className="fill-ink-3 text-[10.5px]">now</text>}
        </svg>
      )}
      {pts.length <= 1 && <p className="pt-6 text-center text-[12.5px] text-ink-3">Not enough history yet</p>}
    </div>
  );
}

export function UsageBar({ used, total, tone = 'normal', children }: { used: number; total: number; tone?: 'normal' | 'warn' | 'critical'; children?: ReactNode }) {
  const pct = total ? Math.min(100, (used / total) * 100) : 0;
  return (
    <div>
      <div className="h-2 overflow-hidden rounded-full bg-sunken" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)}>
        <div className={cn('h-full rounded-full', tone === 'critical' ? 'bg-offline-fill' : tone === 'warn' ? 'bg-attention-fill' : 'bg-series')} style={{ width: `${pct}%` }} />
      </div>
      {children}
    </div>
  );
}

